import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import SalesSummaryCards from "./SalesSummaryCards";
import { toast } from "sonner";
import { Eye, Search, RefreshCw, RotateCcw } from "lucide-react";
import { usePersistedFilters } from "@/hooks/usePersistedState";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

const STATUS_LABEL = {
  completed: { l: "Tamamlandı", cls: "text-emerald-700" },
  voided:    { l: "İptal",      cls: "text-red-600" },
  refunded:  { l: "İade",       cls: "text-amber-700" },
};

// Phase 54.7.B — colour hints for the compact payment method label.
const PAYMENT_CLS = {
  "Nakit":  "text-emerald-700",
  "POS":    "text-sky-700",
  "Havale": "text-violet-700",
};
const paymentClass = (label) => PAYMENT_CLS[label] || (label?.startsWith("Karışık") ? "text-amber-700" : "text-[#71717A]");

const todayISO = () => {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};

const INITIAL_FILTERS = {
  q: "",
  date_from: "",
  date_to: "",
  empId: "",
  branchId: "",
  status: "",
  page: 1,
  pageSize: 50,
  sortBy: "created_at",
  sortDir: "desc",
};

export default function SalesList() {
  const { isSuperAdmin } = useUsmAuth();
  // Phase 54.7.A — persist every filter, search, page & sort across
  // navigations, F5 refreshes and browser restarts. Reset button below
  // is the only way to clear.
  const { filters, updateFilters, resetFilters } = usePersistedFilters(
    "sales",
    INITIAL_FILTERS,
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);

  // Phase 54.7.B (item 4A) — the first time each calendar day the user
  // opens the Sales page, auto-scope to TODAY. Track "last seen day" in
  // localStorage so the override happens exactly once per day and does
  // not clobber a manual date change (users may still clear or edit).
  const [dayApplied, setDayApplied] = useState(false);
  useEffect(() => {
    if (dayApplied) return;
    const today = todayISO();
    let seen = null;
    try { seen = localStorage.getItem("usm.sales.last_seen_day"); } catch { /* ignore */ }
    if (seen !== today) {
      updateFilters({ date_from: today, date_to: today, page: 1 });
      try { localStorage.setItem("usm.sales.last_seen_day", today); } catch { /* ignore */ }
    }
    setDayApplied(true);
  }, []);

  const load = async (overridePage) => {
    setLoading(true);
    try {
      const p = overridePage ?? filters.page;
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.empId) params.set("employee_id", filters.empId);
      if (filters.branchId) params.set("branch_id", filters.branchId);
      if (filters.status) params.set("status", filters.status);
      params.set("page", p);
      params.set("limit", filters.pageSize);
      const r = await usmApi.get(`/sales?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error("Satışlar yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        if (isSuperAdmin) {
          const [b, e] = await Promise.all([usmApi.get("/branches"), usmApi.get("/employees")]);
          setBranches(b.data || []);
          setEmployees(e.data || []);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [isSuperAdmin]);

  // Reload when any *filter* changes — but reset page to 1 in that case.
  useEffect(() => {
    updateFilters({ page: 1 });
    load(1);
  }, [filters.q, filters.date_from, filters.date_to, filters.empId, filters.branchId, filters.status]);

  // Reload when only the page changes.
  useEffect(() => {
    load();
  }, [filters.page, filters.pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));

  return (
    <div data-testid="usm-sales-list">
      <PageHeader title="Satış Geçmişi" breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Satış Geçmişi" }]} />

      {/* Phase 54.25 #5 — Quick operational overview above the filter row.
          Reacts instantly to date/branch/employee/status filter changes. */}
      <SalesSummaryCards filters={filters} />

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input data-testid="sales-search" placeholder="Satış no, müşteri, stok kodu ara..." value={filters.q}
            onChange={(e) => updateFilters({ q: e.target.value })}
            className="w-full pl-9 pr-3 py-2 border border-[#E7E5E4] text-sm" />
        </div>
        <input data-testid="sales-date-from" type="date" value={filters.date_from}
          onChange={(e) => updateFilters({ date_from: e.target.value })}
          className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        <input data-testid="sales-date-to" type="date" value={filters.date_to}
          onChange={(e) => updateFilters({ date_to: e.target.value })}
          className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        <select data-testid="sales-status-filter" value={filters.status} onChange={(e) => updateFilters({ status: e.target.value })} className="px-3 py-2 border border-[#E7E5E4] text-sm">
          <option value="">Tüm durumlar</option>
          <option value="completed">Tamamlandı</option>
          <option value="voided">İptal</option>
          <option value="refunded">İade</option>
        </select>
        {isSuperAdmin && (
          <>
            <select data-testid="sales-branch-filter" value={filters.branchId} onChange={(e) => updateFilters({ branchId: e.target.value })} className="px-3 py-2 border border-[#E7E5E4] text-sm">
              <option value="">Tüm şubeler</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select data-testid="sales-emp-filter" value={filters.empId} onChange={(e) => updateFilters({ empId: e.target.value })} className="px-3 py-2 border border-[#E7E5E4] text-sm">
              <option value="">Tüm çalışanlar</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name || emp.username}</option>)}
            </select>
          </>
        )}
        <button data-testid="sales-refresh" onClick={() => load()} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]" title="Yenile"><RefreshCw size={14} /></button>
        <button data-testid="sales-reset-filters" onClick={resetFilters} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9] text-xs uppercase tracking-[0.1em] text-[#71717A]" title="Filtreleri sıfırla">
          <RotateCcw size={14} className="inline -mt-[2px] mr-1" /> Sıfırla
        </button>
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-3 py-3">Satış No</th>
              <th className="text-left px-3 py-3">Tarih</th>
              <th className="text-left px-3 py-3">Müşteri</th>
              <th className="text-left px-3 py-3">Çalışan</th>
              <th className="text-left px-3 py-3">Şube</th>
              <th className="text-right px-3 py-3">Tutar</th>
              <th className="text-left px-3 py-3">Ödeme</th>
              <th className="text-left px-3 py-3">Durum</th>
              <th className="text-right px-3 py-3 w-20">Detay</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-[#71717A]">Hiç satış yok.</td></tr>
            ) : items.map((s) => {
              const st = STATUS_LABEL[s.status] || { l: s.status, cls: "" };
              const paySummary = s.payment_summary || "—";
              return (
                <tr key={s.id} data-testid={`sales-row-${s.sale_no}`} className="border-t border-[#F5F5F4]">
                  <td className="px-3 py-3 font-mono text-[12px] font-semibold">{s.sale_no}</td>
                  <td className="px-3 py-3 text-[#52525B]">{new Date(s.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-3 py-3">{s.customer_name || <span className="text-[#A8A29E]">—</span>}</td>
                  <td className="px-3 py-3 text-[#52525B]">{s.employee?.full_name || s.employee?.username || "—"}</td>
                  <td className="px-3 py-3 text-[#52525B]">{s.branch?.name || "—"}</td>
                  <td className="px-3 py-3 text-right font-finance">{TRY.format(s.grand_total || 0)}</td>
                  <td className={"px-3 py-3 text-[12px] font-medium " + paymentClass(paySummary)} data-testid={`sales-payment-${s.sale_no}`}>{paySummary}</td>
                  <td className={"px-3 py-3 text-[11px] uppercase tracking-[0.15em] " + st.cls}>● {st.l}</td>
                  <td className="px-3 py-3 text-right">
                    <Link to={`/usm/sales/${s.id}`} data-testid={`sales-detail-${s.sale_no}`} className="p-2 inline-flex hover:bg-[#FAFAF9]"><Eye size={14} /></Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-[#71717A]">
          <span>Toplam {total} satış</span>
          <div className="flex items-center gap-2">
            <button data-testid="sales-page-prev" disabled={filters.page <= 1} onClick={() => updateFilters({ page: filters.page - 1 })} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">‹</button>
            <span data-testid="sales-page-indicator">Sayfa {filters.page}/{totalPages}</span>
            <button data-testid="sales-page-next" disabled={filters.page >= totalPages} onClick={() => updateFilters({ page: filters.page + 1 })} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">›</button>
          </div>
        </div>
      )}
    </div>
  );
}
